var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import React, { useCallback, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { useOkapiKy } from "@folio/stripes/core";
import { Pane, Button, MenuSection } from "@folio/stripes/components";
import { FormattedMessage, useIntl } from "react-intl";
import { useQuery } from "react-query";
import { fetchTitleById } from "../../api/titleService";
import { fetchProductsByTitleId } from "../../api/productService";
import TitleDetail from "./TitleDetail";
import { RecekPaneSet } from "../../components/RecekPaneSet";
import { RecordPreviewModal } from "../../components/RecordPreviewModal";
import { usePermissions } from "../../hooks/usePermissions";
import { getMainTitleName } from "../../utils/titleDisplay";
import { isForbiddenError, retryUnlessForbidden } from "../../utils/httpErrors";
import { useCreateProductConfirm } from "../../hooks/useCreateProductConfirm";
import { CreateAnotherProductConfirmModal } from "../../components/title/CreateAnotherProductConfirmModal";
var TitleDetailPage = function () {
    var _a;
    var history = useHistory();
    var location = useLocation();
    var ky = useOkapiKy();
    var formatMessage = useIntl().formatMessage;
    var _b = usePermissions(), canEditTitleItem = _b.canEditTitleItem, canCreateProductForTitle = _b.canCreateProductForTitle, canPreviewTitle = _b.canPreviewTitle, previewNeedsPublicRecord = _b.previewNeedsPublicRecord;
    var _c = useState(false), isPreviewOpen = _c[0], setIsPreviewOpen = _c[1];
    var urlParams = new URLSearchParams(location.search);
    var titleId = urlParams.get("id") || urlParams.get("detail");
    var _d = useQuery(["title", titleId], function () { return fetchTitleById(ky, titleId); }, {
        enabled: !!titleId,
        retry: retryUnlessForbidden,
    }), title = _d.data, isLoading = _d.isLoading, isError = _d.isError, error = _d.error;
    // A Distributor may preview only a title with a PUBLIC product (RECEK-76074).
    // Same query as the products panel of the detail, so it is usually cached.
    var titleProducts = useQuery(["products", titleId], function () { return fetchProductsByTitleId(ky, titleId); }, { enabled: !!titleId && previewNeedsPublicRecord }).data;
    var openCreateProduct = useCallback(function (id) { return history.push("/bragency/titles/edit/".concat(id, "?newProduct=1")); }, [history]);
    var _e = useCreateProductConfirm(openCreateProduct), requestCreateProduct = _e.requestCreate, createProductConfirmModalProps = _e.modalProps;
    var handleBackToList = function () {
        history.goBack();
    };
    if (!titleId) {
        return (React.createElement(RecekPaneSet, null,
            React.createElement(Pane, { defaultWidth: "fill", paneTitle: formatMessage({
                    id: "ui-br-agency.title.detail.pane.title",
                }) },
                React.createElement("p", null, formatMessage({ id: "ui-br-agency.title.id.not.provided" })),
                React.createElement(Button, { buttonStyle: "primary", onClick: handleBackToList },
                    React.createElement(FormattedMessage, { id: "ui-br-agency.button.back" })))));
    }
    if (isLoading) {
        return (React.createElement(RecekPaneSet, null,
            React.createElement(Pane, { defaultWidth: "fill", paneTitle: formatMessage({
                    id: "ui-br-agency.title.detail.pane.title",
                }) },
                React.createElement(FormattedMessage, { id: "ui-br-agency.loading" }))));
    }
    if (isError || !title) {
        return (React.createElement(RecekPaneSet, null,
            React.createElement(Pane, { defaultWidth: "fill", paneTitle: formatMessage({
                    id: "ui-br-agency.title.detail.pane.title",
                }) },
                React.createElement("p", null, isForbiddenError(error)
                    ? formatMessage({ id: "ui-br-agency.error.access.denied" })
                    : (error === null || error === void 0 ? void 0 : error.message) ||
                        formatMessage({ id: "ui-br-agency.title.not.found" })),
                React.createElement(Button, { buttonStyle: "primary", onClick: handleBackToList },
                    React.createElement(FormattedMessage, { id: "ui-br-agency.button.back" })))));
    }
    var primaryName = ((_a = getMainTitleName(title.names)) === null || _a === void 0 ? void 0 : _a.name) || "";
    var canPreview = canPreviewTitle(title, titleProducts === null || titleProducts === void 0 ? void 0 : titleProducts.items);
    return (React.createElement(RecekPaneSet, null,
        React.createElement(Pane, { defaultWidth: "fill", paneTitle: primaryName
                ? formatMessage({ id: "ui-br-agency.title.detail.pane.title.named" }, { name: primaryName })
                : formatMessage({ id: "ui-br-agency.title.detail.pane.title" }), firstMenu: React.createElement(Button, { buttonStyle: "default", onClick: handleBackToList, marginBottom0: true },
                React.createElement(FormattedMessage, { id: "ui-br-agency.button.back" })), actionMenu: !canEditTitleItem(title) &&
                !canCreateProductForTitle(title) &&
                !canPreview
                ? undefined
                : function (_a) {
                    var onToggle = _a.onToggle;
                    return (React.createElement(MenuSection, { label: "Akce", id: "actions-menu" },
                        canEditTitleItem(title) && (React.createElement(Button, { buttonStyle: "dropdownItem", onClick: function () {
                                return history.push("/bragency/titles/edit/".concat(title.id));
                            }, fullWidth: true },
                            React.createElement(FormattedMessage, { id: "ui-br-agency.button.edit" }))),
                        canCreateProductForTitle(title) && (React.createElement(Button, { buttonStyle: "dropdownItem", onClick: function () {
                                onToggle === null || onToggle === void 0 ? void 0 : onToggle();
                                // TitleDetail renders the products panel, so the
                                // product count is usually already cached.
                                requestCreateProduct(title.id);
                            }, fullWidth: true },
                            React.createElement(FormattedMessage, { id: "ui-br-agency.title.action.createProduct" }))),
                        canPreview && (React.createElement(Button, { buttonStyle: "dropdownItem", onClick: function () { return setIsPreviewOpen(true); }, fullWidth: true },
                            React.createElement(FormattedMessage, { id: "ui-br-agency.preview.action" })))));
                } },
            React.createElement(TitleDetail, { title: title })),
        React.createElement(CreateAnotherProductConfirmModal, __assign({}, createProductConfirmModalProps)),
        React.createElement(RecordPreviewModal, { open: isPreviewOpen, target: { kind: "title", id: title.id }, onClose: function () { return setIsPreviewOpen(false); } })));
};
export default TitleDetailPage;
